import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Loader2, Power, PowerOff, Send, Users, Clock, Mail,
  Plus, Trash2, Save, Play, ChevronDown, ChevronUp, Zap,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function ReengagementManager({ darkMode: dm }) {
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [showLog, setShowLog] = useState(false);

  const headers = { Authorization: `Bearer ${localStorage.getItem("gmp_token")}` };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [cfgRes, statsRes, logRes] = await Promise.all([
        axios.get(`${API}/admin/reengagement/config`, { headers }),
        axios.get(`${API}/admin/reengagement/stats`, { headers }),
        axios.get(`${API}/admin/reengagement/log`, { headers }),
      ]);
      setConfig(cfgRes.data);
      setStats(statsRes.data);
      setLog(logRes.data);
    } catch { toast.error("Failed to load re-engagement config"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const saveConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/reengagement/config`, config, { headers });
      toast.success("Configuration saved");
      fetchAll();
    } catch { toast.error("Failed to save config"); }
    finally { setSaving(false); }
  };

  const toggleEnabled = async () => {
    const newState = !config.is_enabled;
    try {
      await axios.put(`${API}/admin/reengagement/config`, { is_enabled: newState }, { headers });
      setConfig(c => ({ ...c, is_enabled: newState }));
      toast.success(newState ? "Re-engagement enabled" : "Re-engagement disabled");
      fetchAll();
    } catch { toast.error("Failed to toggle"); }
  };

  const triggerNow = async () => {
    setTriggering(true);
    try {
      const res = await axios.post(`${API}/admin/reengagement/trigger`, {}, { headers });
      toast.success(`Sent ${res.data.sent} re-engagement emails (checked ${res.data.checked})`);
      fetchAll();
    } catch { toast.error("Trigger failed"); }
    finally { setTriggering(false); }
  };

  const updateStep = (idx, field, value) => {
    const newSeries = [...(config.series || [])];
    newSeries[idx] = { ...newSeries[idx], [field]: value };
    setConfig(c => ({ ...c, series: newSeries }));
  };

  const addStep = () => {
    const step = (config.series?.length || 0) + 1;
    setConfig(c => ({
      ...c,
      series: [...(c.series || []), {
        step,
        delay_days: 7,
        subject: `Re-engagement email #${step}`,
        body_html: `<p>Hello {{name}},</p><p>We'd love to see you back!</p>`,
      }],
    }));
  };

  const removeStep = (idx) => {
    const newSeries = (config.series || []).filter((_, i) => i !== idx);
    setConfig(c => ({ ...c, series: newSeries.map((s, i) => ({ ...s, step: i + 1 })) }));
  };

  if (loading || !config) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="reengagement-manager">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className={`text-lg font-bold ${dm ? "text-gray-100" : "text-gray-900"}`}>
            Re-engagement Campaigns
          </h3>
          <p className={`text-xs mt-0.5 ${dm ? "text-gray-400" : "text-gray-500"}`}>
            Automated win-back email series for low-engagement subscribers
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={toggleEnabled}
            className={`rounded-xl text-xs ${config.is_enabled ? "border-emerald-500 text-emerald-600" : dm ? "border-gray-600 text-gray-400" : ""}`}
            data-testid="toggle-reengagement-btn"
          >
            {config.is_enabled ? <Power className="w-3.5 h-3.5 mr-1" /> : <PowerOff className="w-3.5 h-3.5 mr-1" />}
            {config.is_enabled ? "Enabled" : "Disabled"}
          </Button>
          <Button
            size="sm"
            onClick={triggerNow}
            disabled={triggering}
            className="bg-[#e0482d] hover:bg-[#c93d25] text-white rounded-xl text-xs"
            data-testid="trigger-reengagement-btn"
          >
            {triggering ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Play className="w-3.5 h-3.5 mr-1" />}
            Run Now
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard dm={dm} label="Total Sent" value={stats.total_emails_sent} icon={Send} color="text-blue-500" bg="bg-blue-500/10" />
          <StatCard dm={dm} label="Unique Recipients" value={stats.unique_recipients} icon={Users} color="text-emerald-500" bg="bg-emerald-500/10" />
          <StatCard dm={dm} label="In Cooldown" value={stats.in_cooldown} icon={Clock} color="text-amber-500" bg="bg-amber-500/10" />
          <StatCard dm={dm} label="Score Threshold" value={`<${stats.score_threshold}`} icon={Zap} color="text-red-500" bg="bg-red-500/10" />
        </div>
      )}

      {/* Settings */}
      <Card className={`rounded-2xl border ${dm ? "bg-gray-800 border-gray-700" : "border-gray-200"}`}>
        <CardHeader className="pb-2">
          <CardTitle className={`text-sm ${dm ? "text-gray-100" : ""}`}>Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={`text-xs font-semibold mb-1.5 block ${dm ? "text-gray-300" : "text-gray-600"}`}>
                Score Threshold (send to subscribers below this score)
              </label>
              <Input
                type="number"
                min={1}
                max={100}
                value={config.score_threshold}
                onChange={e => setConfig(c => ({ ...c, score_threshold: parseInt(e.target.value) || 50 }))}
                className={`rounded-xl ${dm ? "bg-gray-700 border-gray-600 text-gray-100" : ""}`}
                data-testid="threshold-input"
              />
            </div>
            <div>
              <label className={`text-xs font-semibold mb-1.5 block ${dm ? "text-gray-300" : "text-gray-600"}`}>
                Cooldown Period (days before re-sending to same subscriber)
              </label>
              <Input
                type="number"
                min={1}
                max={365}
                value={config.cooldown_days}
                onChange={e => setConfig(c => ({ ...c, cooldown_days: parseInt(e.target.value) || 30 }))}
                className={`rounded-xl ${dm ? "bg-gray-700 border-gray-600 text-gray-100" : ""}`}
                data-testid="cooldown-input"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Email Series */}
      <Card className={`rounded-2xl border ${dm ? "bg-gray-800 border-gray-700" : "border-gray-200"}`}>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className={`text-sm ${dm ? "text-gray-100" : ""}`}>
                <Mail className="w-4 h-4 inline mr-1.5" />
                Email Series ({config.series?.length || 0} steps)
              </CardTitle>
              <CardDescription className={`text-xs ${dm ? "text-gray-400" : ""}`}>
                Emails sent in sequence to low-engagement subscribers
              </CardDescription>
            </div>
            <Button size="sm" variant="outline" onClick={addStep} className={`rounded-lg text-xs ${dm ? "border-gray-600 text-gray-300" : ""}`} data-testid="add-step-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add Step
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {(config.series || []).map((step, i) => (
            <div key={i} className={`p-4 rounded-xl border ${dm ? "bg-gray-900/50 border-gray-700" : "bg-gray-50 border-gray-200"}`} data-testid={`series-step-${i}`}>
              <div className="flex items-center justify-between mb-3">
                <span className={`text-xs font-bold ${dm ? "text-gray-200" : "text-gray-700"}`}>
                  Step {i + 1} {i === 0 ? "(Sent immediately)" : `(After ${step.delay_days} days)`}
                </span>
                {config.series.length > 1 && (
                  <Button size="sm" variant="ghost" onClick={() => removeStep(i)} className="text-red-500 h-6 w-6 p-0" data-testid={`remove-step-${i}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
              {i > 0 && (
                <div className="mb-3">
                  <label className={`text-[10px] font-semibold mb-1 block ${dm ? "text-gray-400" : "text-gray-500"}`}>Delay (days after previous step)</label>
                  <Input
                    type="number" min={1} max={90}
                    value={step.delay_days}
                    onChange={e => updateStep(i, "delay_days", parseInt(e.target.value) || 7)}
                    className={`rounded-lg h-8 text-xs ${dm ? "bg-gray-700 border-gray-600 text-gray-100" : ""}`}
                  />
                </div>
              )}
              <div className="mb-3">
                <label className={`text-[10px] font-semibold mb-1 block ${dm ? "text-gray-400" : "text-gray-500"}`}>Subject Line</label>
                <Input
                  value={step.subject}
                  onChange={e => updateStep(i, "subject", e.target.value)}
                  placeholder="We miss you, {{name}}!"
                  className={`rounded-lg h-8 text-xs ${dm ? "bg-gray-700 border-gray-600 text-gray-100" : ""}`}
                />
              </div>
              <div>
                <label className={`text-[10px] font-semibold mb-1 block ${dm ? "text-gray-400" : "text-gray-500"}`}>Body (HTML)</label>
                <textarea
                  value={step.body_html}
                  onChange={e => updateStep(i, "body_html", e.target.value)}
                  rows={2}
                  className={`w-full rounded-lg border px-2 py-1.5 text-xs ${dm ? "bg-gray-700 border-gray-600 text-gray-100 placeholder:text-gray-500" : "border-gray-200"}`}
                />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Save */}
      <div className="flex justify-end">
        <Button onClick={saveConfig} disabled={saving} className="bg-[#e0482d] hover:bg-[#c93d25] text-white rounded-xl" data-testid="save-config-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Save className="w-4 h-4 mr-1.5" />}
          Save Configuration
        </Button>
      </div>

      {/* Send Log */}
      <Card className={`rounded-2xl border ${dm ? "bg-gray-800 border-gray-700" : "border-gray-200"}`}>
        <CardHeader className="pb-1">
          <button
            onClick={() => setShowLog(!showLog)}
            className="flex items-center justify-between w-full"
            data-testid="toggle-log-btn"
          >
            <CardTitle className={`text-sm ${dm ? "text-gray-100" : ""}`}>
              Send Log ({log.length} entries)
            </CardTitle>
            {showLog ? <ChevronUp className={`w-4 h-4 ${dm ? "text-gray-400" : ""}`} /> : <ChevronDown className={`w-4 h-4 ${dm ? "text-gray-400" : ""}`} />}
          </button>
        </CardHeader>
        {showLog && (
          <CardContent>
            {log.length === 0 ? (
              <p className={`text-xs text-center py-6 ${dm ? "text-gray-500" : "text-gray-400"}`}>No emails sent yet</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="reengagement-log-table">
                  <thead>
                    <tr className={`border-b ${dm ? "border-gray-700" : "border-gray-100"}`}>
                      <th className={`text-left py-2 px-3 font-semibold ${dm ? "text-gray-300" : "text-gray-600"}`}>Email</th>
                      <th className={`text-center py-2 px-3 font-semibold ${dm ? "text-gray-300" : "text-gray-600"}`}>Step</th>
                      <th className={`text-center py-2 px-3 font-semibold ${dm ? "text-gray-300" : "text-gray-600"}`}>Score</th>
                      <th className={`text-right py-2 px-3 font-semibold ${dm ? "text-gray-300" : "text-gray-600"}`}>Sent</th>
                    </tr>
                  </thead>
                  <tbody>
                    {log.slice(0, 20).map((l, i) => (
                      <tr key={i} className={`border-b ${dm ? "border-gray-700/50" : "border-gray-50"}`}>
                        <td className={`py-2 px-3 font-mono ${dm ? "text-gray-200" : "text-gray-800"}`}>{l.email}</td>
                        <td className="py-2 px-3 text-center">
                          <Badge className="text-[9px] px-1.5 bg-blue-500/10 text-blue-500">Step {l.step}</Badge>
                        </td>
                        <td className={`py-2 px-3 text-center font-mono ${dm ? "text-gray-400" : "text-gray-500"}`}>{l.score_at_send}</td>
                        <td className={`py-2 px-3 text-right ${dm ? "text-gray-500" : "text-gray-400"}`}>
                          {new Date(l.sent_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        )}
      </Card>
    </div>
  );
}


function StatCard({ dm, label, value, icon: Icon, color, bg }) {
  return (
    <Card className={`rounded-xl border ${dm ? "bg-gray-800 border-gray-700" : "border-gray-200"}`}>
      <CardContent className="p-3">
        <div className="flex items-center gap-2.5">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${bg}`}>
            <Icon className={`w-4 h-4 ${color}`} />
          </div>
          <div>
            <p className={`text-[10px] font-medium uppercase tracking-wider ${dm ? "text-gray-400" : "text-gray-500"}`}>{label}</p>
            <p className={`text-lg font-bold ${dm ? "text-gray-100" : "text-gray-900"}`}>{value}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
